import { FormEvent, KeyboardEvent, useRef, useState } from "react";
import { CornerDownLeft, X } from "lucide-react";
import { CHAT_MESSAGE_MAX_LENGTH } from "@cliently/types";
import {
  Button,
  EmojiPickerButton,
  insertTextAtCursor,
  useAutosizeTextarea,
} from "@cliently/ui";

interface MessageInputProps {
  onSend: (content: string) => void;
  disabled?: boolean;
  replyPreview?: { senderLabel?: string | null; content: string } | null;
  onCancelReply?: () => void;
}



export const MessageInput = ({
  onSend,
  disabled = false,
  replyPreview,
  onCancelReply,
}: MessageInputProps) => {
  const [content, setContent] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextarea(textareaRef, content);

  const submitMessage = () => {
    const trimmed = content.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setContent("");
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitMessage();
  };

  const handleTextareaKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.ctrlKey && event.key === "Enter") {
      event.preventDefault();
      submitMessage();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border-t p-3">
      <div className="flex items-start gap-2">
        <EmojiPickerButton
          disabled={disabled}
          onEmojiSelect={(emoji) =>
            insertTextAtCursor(
              textareaRef.current,
              emoji,
              content,
              setContent,
              CHAT_MESSAGE_MAX_LENGTH,
            )
          }
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {replyPreview && (
            <div className="bg-muted/50 flex items-start justify-between gap-2 rounded-md border px-3 py-2">
              <div className="min-w-0">
                <p className="text-xs font-medium">
                  Réponse à {replyPreview.senderLabel || "message"}
                </p>
                <p className="text-muted-foreground line-clamp-1 text-xs">
                  {replyPreview.content}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onCancelReply}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Annuler la réponse"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="flex items-stretch gap-2">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(event) =>
                setContent(
                  event.target.value.slice(0, CHAT_MESSAGE_MAX_LENGTH),
                )
              }
              onKeyDown={handleTextareaKeyDown}
              placeholder="Tapez votre message..."
              rows={1}
              maxLength={CHAT_MESSAGE_MAX_LENGTH}
              disabled={disabled}
              className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring min-h-[44px] max-h-[220px] w-full resize-none rounded-md border px-3 py-2 text-sm leading-normal outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <Button
              type="submit"
              disabled={disabled || !content.trim()}
              className="min-h-[44px] self-stretch disabled:cursor-not-allowed"
              title="Envoyer (Ctrl + Entrée)"
              aria-label="Envoyer (Ctrl + Entrée)"
            >
              <span>Envoyer</span>
              <CornerDownLeft className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-muted-foreground text-right text-[11px] tabular-nums">
            {content.length}/{CHAT_MESSAGE_MAX_LENGTH}
          </p>
        </div>
      </div>
    </form>
  );
}
